// src/components/ProfileRecipientPopup.jsx
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FaTimes, FaSearch } from 'react-icons/fa';
import { readGlobalProfiles } from '@services/firebaseGlobal';
import { readUserData } from '@services/firebaseGlobal';
import { getCurrentUserEnv } from '@services/firebase';

const ProfileRecipientPopup = ({ isOpen, onClose, onSelectRecipient, currentUser }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadUsers();
    }
  }, [isOpen]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const userList = await readGlobalProfiles('cUsers');
      const allProfilesRaw = await readUserData('profile');

      const currentEnv = getCurrentUserEnv();
      const usersWithProfiles = userList
        .filter(userData => (userData.env || 'Prod') === currentEnv)
        .map(userData => {
          const profile = allProfilesRaw.find(p => p.id === userData.id);
          
          let role = 'User';
          if (userData.isadminuser) role = 'Admin';
          else if (userData.issuperuser) role = 'Super';
          else if (userData.isbuyer) role = 'Buyer';
          else if (userData.ismover) role = 'Mover';

          return {
            id: userData.id,
            email: userData.email || profile?.email || 'No email',
            userName: profile?.f_name || 'Unknown',
            userSurname: profile?.l_name || 'User',
            profilePicture: profile?.p_picture || '',
            userRole: role
          };
        })
        .filter(userItem => userItem.id !== currentUser?.uid); // Exclude current user

      setUsers(usersWithProfiles);
      setFilteredUsers(usersWithProfiles);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase();
      const filtered = users.filter(user => {
        const displayName = `${user.userName} ${user.userSurname}`.toLowerCase();
        const email = user.email.toLowerCase();
        const role = user.userRole.toLowerCase();
        
        return displayName.includes(query) || 
               email.includes(query) || 
               role.includes(query);
      });
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  const handleUserSelect = (user) => {
    onSelectRecipient(user);
    onClose();
  };

  const getUserDisplayName = (user) => {
    return `${user.userName} ${user.userSurname}`;
  };

  const getProfileImage = (profilePicture) => {
    return profilePicture && profilePicture.trim() !== '' ? profilePicture : null;
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="popup-overlay" onClick={onClose}>
      <style>{`
        .popup-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 2000;
          padding: 20px;
        }

        .recipient-popup {
          background: var(--bg-surface, white);
          border-radius: 12px;
          width: 90vw;
          max-width: 500px;
          max-height: 80vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        }

        .popup-header {
          padding: 20px;
          border-bottom: 1px solid var(--border-color, #f0f0f0);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .popup-title {
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary, #333);
          margin: 0;
        }

        .popup-close-btn {
          background: none;
          border: none;
          font-size: 18px;
          cursor: pointer;
          color: var(--text-secondary, #666);
          padding: 5px;
          border-radius: 50%;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .popup-close-btn:hover {
          background: var(--bg-section, #f5f5f5);
          color: var(--text-primary, #333);
        }

        .search-section {
          padding: 15px 20px;
          border-bottom: 1px solid var(--border-color, #f0f0f0);
        }

        .search-input-wrapper {
          background: var(--bg-section, #f8f9fa);
          border-radius: 8px;
          padding: 10px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .search-icon {
          color: var(--text-secondary, #666);
          font-size: 14px;
        }

        .search-input {
          flex: 1;
          border: none;
          background: transparent;
          font-size: 14px;
          outline: none;
          color: var(--text-primary, #333);
        }

        .search-input::placeholder {
          color: var(--text-muted, #999);
        }

        .users-list {
          flex: 1;
          overflow-y: auto;
          max-height: 400px;
        }

        .user-item {
          display: flex;
          align-items: center;
          padding: 15px 20px;
          gap: 12px;
          cursor: pointer;
          border-bottom: 1px solid var(--border-color, #f0f0f0);
          transition: background-color 0.2s;
        }



        .user-item:last-child {
          border-bottom: none;
        }

        .user-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          overflow: hidden;
          flex-shrink: 0;
        }

        .user-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .avatar-placeholder {
          width: 100%;
          height: 100%;
          background: var(--bg-section, #e0e0e0);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-secondary, #666);
          font-weight: 500;
          font-size: 14px;
        }

        .user-info {
          flex: 1;
          min-width: 0;
        }

        .user-name {
          font-weight: 500;
          font-size: 14px;
          color: var(--text-primary, #333);
          margin-bottom: 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .user-details {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .user-email {
          font-size: 12px;
          color: var(--text-secondary, #666);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          flex: 1;
          margin-right: 10px;
        }

        .user-role {
          font-size: 11px;
          padding: 2px 8px;
          border-radius: 12px;
          font-weight: 500;
          white-space: nowrap;
        }

        .user-role.Admin { background: #e3f2fd; color: #1976d2; }
        .user-role.Super { background: #f3e5f5; color: #7b1fa2; }
        .user-role.Buyer { background: #e8f5e8; color: #388e3c; }
        .user-role.Mover { background: #fff3e0; color: #f57c00; }
        .user-role.User { background: #f5f5f5; color: #666; }

        .loading-state, .empty-state {
          padding: 40px 20px;
          text-align: center;
          color: var(--text-secondary, #666);
        }

        .empty-state {
          font-size: 14px;
        }

        /* ── Dark mode ── */
        html[data-theme="dark"] .recipient-popup {
          box-shadow: 0 10px 30px rgba(0,0,0,0.6);
        }

        html[data-theme="dark"] .user-role.Admin {
          background: rgba(25,118,210,0.18);
          color: #90caf9;
        }
        html[data-theme="dark"] .user-role.Super {
          background: rgba(123,31,162,0.18);
          color: #ce93d8;
        }
        html[data-theme="dark"] .user-role.Buyer {
          background: rgba(56,142,60,0.18);
          color: #a5d6a7;
        }
        html[data-theme="dark"] .user-role.Mover {
          background: rgba(245,124,0,0.18);
          color: #ffcc80;
        }
        html[data-theme="dark"] .user-role.User {
          background: var(--bg-section);
          color: var(--text-muted);
        }
      `}</style>
      
      <div className="recipient-popup" onClick={(e) => e.stopPropagation()}>
        <div className="popup-header">
          <h3 className="popup-title">Select Recipient</h3>
          <button className="popup-close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="search-section">
          <div className="search-input-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Search users by name, email or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="users-list">
          {loading ? (
            <div className="loading-state">Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="empty-state">
              {searchTerm ? 'No users found matching your search' : 'No users available'}
            </div>
          ) : (
            filteredUsers.map(user => (
              <div
                key={user.id}
                className="user-item"
                onClick={() => handleUserSelect(user)}
              >
                <div className="user-avatar">
                  {getProfileImage(user.profilePicture) ? (
                    <img src={getProfileImage(user.profilePicture)} alt="Profile" />
                  ) : (
                    <div className="avatar-placeholder">
                      {user.userName?.charAt(0) || 'U'}
                    </div>
                  )}
                </div>
                <div className="user-info">
                  <div className="user-name">
                    {getUserDisplayName(user)}
                  </div>
                  <div className="user-details">
                    <span className="user-email">{user.email}</span>
                    <span className={`user-role ${user.userRole}`}>
                      {user.userRole}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>,
    document.getElementById('portal-root')
  );
};

export default ProfileRecipientPopup;